/**
 * B31a — the FRAME side of the §9.2 postMessage bridge: the embedded
 * storefront announces `ready`, receives the host's `init` (site key,
 * preselect, theme), and reports resize/success/cancel back — and, through
 * the host error relay below, that the frame failed (B552).
 *
 * Trust model, deliberate:
 * - only messages whose source is the embedding parent window are read;
 * - the host origin is pinned to the FIRST valid init — later inits from
 *   other origins are dead (an established bridge cannot be hijacked);
 * - outbound messages go to the pinned origin exactly; the only thing
 *   ever posted to "*" is the empty `ready` ping (it carries nothing);
 * - everything inbound passes the fail-closed protocol parser, so theme
 *   hints arrive pre-sanitized.
 *
 * The REAL §9.2 walls — frame-ancestors CSP and the API-side
 * origin ↔ site-key binding — live server-side; this module is the
 * cooperative half that makes embedding work at all.
 */
import {
  cancelMessage,
  closeMessage,
  errorMessage,
  openMessage,
  parseHostMessage,
  readyMessage,
  resizeMessage,
  successMessage,
  trackMessage,
  type InitMessage,
  type TrackEvent,
  type ThemeHints,
} from "@transact/embed";
import type { StorefrontContext } from "./context.js";

export interface PostTargetLike {
  postMessage(data: unknown, targetOrigin: string): void;
}

export interface BridgeMessageEventLike {
  readonly data: unknown;
  readonly origin: string;
  readonly source: unknown;
}

export interface BridgeWindowLike {
  readonly parent: PostTargetLike;
  addEventListener(type: "message", fn: (e: BridgeMessageEventLike) => void): void;
  removeEventListener(type: "message", fn: (e: BridgeMessageEventLike) => void): void;
  /** B517 — the ready re-ping's clock (absent in fakes that do not care). */
  setTimeout?(fn: () => void, ms: number): unknown;
  clearTimeout?(handle: unknown): void;
}

export interface Bridge {
  /** The origin pinned by the first valid init; undefined until then. */
  hostOrigin(): string | undefined;
  sendResize(height: number): void;
  sendSuccess(orderId: string): void;
  sendCancel(): void;
  /** B517 — the inline list asks the host for the booking dialog. */
  sendOpen(productId: string): void;
  /** B517 — the dialog's own close door. */
  sendClose(): void;
  /**
   * B524 — one GA4 event for the host page's dataLayer. Queued (bounded)
   * until an init pins the host, because the first events — the list's
   * view — fire before the handshake completes, and an unpinned post is dropped.
   */
  sendTrack(event: TrackEvent): void;
  disconnect(): void;
}

/**
 * B552 (c2-embed) — the host hears that the frame failed. The relay lives
 * APART from the bridge on purpose: a render crash unmounts the App, the
 * unmount runs the bridge's cleanup (disconnect), and the boundary's report
 * resolves after that — so a relay that died with the bridge would be
 * silent exactly when it exists to speak. It remembers only the host the
 * bridge pinned (never "*": the Ref is ours to hand to that host alone) and
 * speaks at most HOST_ERRORS_MAX times per page, so a crash loop cannot
 * flood the tenant's page.
 */
export const HOST_ERRORS_MAX = 3;

export interface HostErrorRelay {
  /** The bridge, when an init pins the host. */
  pin(target: PostTargetLike, origin: string): void;
  /** `{code, ref?}` to the pinned host; nothing before a pin, nothing past the cap. */
  send(code: string, ref?: string): void;
}

export function createHostErrorRelay(): HostErrorRelay {
  let host: { readonly target: PostTargetLike; readonly origin: string } | undefined;
  let sent = 0;
  return {
    pin(target, origin) {
      host = { target, origin };
    },
    send(code, ref) {
      if (host === undefined || sent >= HOST_ERRORS_MAX) return;
      sent += 1;
      try {
        host.target.postMessage(errorMessage(code, ref), host.origin);
      } catch {
        /* a detached parent must not turn a reported crash into a second one */
      }
    },
  };
}

/** The page's one relay: App hands it to the bridge, main.tsx's boundary speaks through it. */
export const hostErrors: HostErrorRelay = createHostErrorRelay();

const READY_PINGS = 20;
const TRACK_QUEUE = 50;
const READY_PING_MS = 250;

export function connectBridge(win: BridgeWindowLike, onInit: (init: InitMessage) => void, relay?: HostErrorRelay): Bridge {
  let pinned: string | undefined;
  let connected = true;
  const queued: TrackEvent[] = [];

  const onMessage = (e: BridgeMessageEventLike): void => {
    if (e.source !== win.parent) return;
    const init = parseHostMessage(e.data);
    if (init === undefined) return;
    if (pinned === undefined) {
      pinned = e.origin;
      relay?.pin(win.parent, pinned);
      for (const t of queued.splice(0)) win.parent.postMessage(trackMessage(t), pinned);
    } else if (e.origin !== pinned) return;
    onInit(init);
  };

  win.addEventListener("message", onMessage);
  // The ready ping is the only "*" post: it carries no data and exists so
  // the loader knows when to deliver init (the frame may load after it).
  win.parent.postMessage(readyMessage(), "*");
  /* B517 (measured on the Navy Pier demo, 2026-09-22): the loader's
     immediate answer to the first ping was intermittently never pinned —
     a hand-posted init a moment later always was — and an unpinned bridge
     posts NOTHING, so the list's card press and every resize went nowhere.
     Re-ping until an init pins the host, bounded, so a page framed by
     something that is not our loader goes quiet. The loader answers every
     ready, and a repeated init from the pinned origin is harmless. */
  let pings = 0;
  let timer: unknown;
  const reping = (): void => {
    if (!connected || pinned !== undefined || pings >= READY_PINGS) return;
    pings += 1;
    win.parent.postMessage(readyMessage(), "*");
    timer = win.setTimeout?.(reping, READY_PING_MS);
  };
  timer = win.setTimeout?.(reping, READY_PING_MS);

  const post = (data: unknown): void => {
    if (!connected || pinned === undefined) return;
    win.parent.postMessage(data, pinned);
  };

  return {
    hostOrigin: () => pinned,
    sendResize: (height) => post(resizeMessage(height)),
    sendSuccess: (orderId) => post(successMessage(orderId)),
    sendCancel: () => post(cancelMessage()),
    sendOpen: (productId) => post(openMessage(productId)),
    sendClose: () => post(closeMessage()),
    sendTrack(event) {
      if (!connected) return;
      if (pinned === undefined) {
        if (queued.length < TRACK_QUEUE) queued.push(event);
        return;
      }
      post(trackMessage(event));
    },
    disconnect(): void {
      if (!connected) return;
      connected = false;
      if (timer !== undefined) win.clearTimeout?.(timer);
      win.removeEventListener("message", onMessage);
    },
  };
}

/** The host page is authoritative for its own embed: init overrides URL context. */
export function mergeInit(ctx: StorefrontContext, init: InitMessage): StorefrontContext {
  return {
    ...ctx,
    siteKey: init.siteKey,
    preselect: init.preselect !== undefined ? init.preselect : ctx.preselect,
  };
}

/** Sanitized theme hints → namespaced CSS custom properties. */
export function themeToCssVars(theme: ThemeHints | undefined): readonly (readonly [string, string])[] {
  if (theme === undefined) return [];
  return Object.entries(theme).map(([key, value]) => [`--transact-${key}`, value] as const);
}
